import SettingsAppState from '../../apps/settings/state'
import getStorage from '../../storage/index'
import { PAGE_STATES } from './pageState'
import RegistrationsState from './registrations'
import { NotFoundError } from './util/NotFoundError'

function resetLocalData () {
  this.set({
    settings: getStorage(this.get('user'), 'setting')
  })

  // Propagate theme change event
  this.get('settings').on('change:theme', (model, theme) => {
    this.trigger('change:theme', model, theme)
  })

  this.trigger('change:theme', this.get('settings'), this.get('settings').get('theme'))
}

/**
 * The pageState is loading async, so wait for that then update the state appropriately
 * dropping anything that comes in after another page change
 * @param {Promise<State>} pageStateResponse Promise resolving to new state properties
 * @param {number} pageLoadCount Which page load this was for
 */
function handleAsyncPageState (pageStateResponse, pageLoadCount) {
  pageStateResponse.then((stateProps) => {
    // Check it's the same page load before updating
    if (pageLoadCount === this.changePageCounter) {
      this.set(stateProps)
    } else {
      console.log('Scrapping async state update as page changed')
    }
  }).catch((e) => {
    // Check it's the same page load before updating
    if (pageLoadCount === this.changePageCounter) {
      if (e instanceof NotFoundError) {
        this.changePage('not_found')
      } else if (e.status === 0) {
        this.trigger('notify:error', 'offline', e)
      } else {
        console.error('Error loading page async state', e)
        window.Rollbar?.error('Error loading page async state', e)
        this.trigger('notify:error', 'unknown', e)
      }
    } else {
      console.warn('Ignoring async state error as page changed', e)
    }
  })
}

/**
 * Tracks application state
 *
 * @event change:theme Theme change
 *  @property {String} name Theme name
 *
 * @event session:start User logged in and model hydrated
 *  @property {Model} user User model
 *
 * @event session:logout User logged out
 * @event session:anonymous No user on startup
 *
 */
export default class State extends Backbone.Model {
  get defaults () {
    return {
      role: 'participant',
      page: null,
      settings: null,
      user: null,
      participant: null,
      session: null,
      activePresenter: null,
      pageState: null,
      // general app ready state
      ready: false
    }
  }

  get role () {
    return this.get('role')
  }

  get defaultPage () {
    return 'home'
  }

  get page () {
    return this.get('page')
  }

  get user () {
    return this.get('user')
  }

  get participant () {
    return this.get('participant')
  }

  get session () {
    return this.get('session')
  }

  get activePresenter () {
    return this.get('activePresenter')
  }

  get pageState () {
    return this.get('pageState')
  }

  get hiddenTabBarItems () {
    if (this.get('user').id) {
      return 'log in'
    } else {
      return 'settings'
    }
  }

  get loggedIn () {
    return !!this.get('user').get('id')
  }

  initialize (attrs = {}, options = {}) {
    // Track states for apps
    this.appStates = {
      settings: new SettingsAppState(),
      registrations: new RegistrationsState({ user: this.user })
    }

    // Track which page change this is so we can ignore some async actions
    this.changePageCounter = 0

    // Session is fully loaded with user
    this.on('session:start session:anonymous', () => {
      resetLocalData.call(this)

      // Session started. Set app to ready state
      if (this.get('participant').get('must_consent')) {
        // check whether we should show the terms page first
        this.set({
          ready: true,
          page: 'terms_agreement'
        })
      } else {
        this.set({ ready: true })
      }
    })

    this.on('session:logout', () => {
      this.changePage(this.defaultPage)
    })

    // Nicety
    this.get('session').on('session:stopped', () => {
      this.trigger('session:logout')
    })

    this.get('session').on('session:started', () => {
      // When the page changes, we're assuming the app is ready
      // Session started. Set app to ready state
      this.set({ ready: true })

      // Session start could have an anonymous user
      if (this.get('user').id) {
        this.trigger('session:start', this.get('user'))
      } else {
        this.trigger('session:anonymous')
      }
    })
  }

  changePage (
    page,
    pageStateOpts = {},
    { maintainPresenter = false, replacePath = false } = {}
  ) {
    const pageLoadCount = ++this.changePageCounter
    const pageStateResponse = PAGE_STATES[page]?.call(this, pageStateOpts)
    let extraState = {}

    if (pageStateResponse?.then) {
      // A promise was returned for this page, so we'll wait for that
      // then update the state with that response later
      handleAsyncPageState.call(this, pageStateResponse, pageLoadCount)
    } else if (typeof pageStateResponse === 'object') {
      extraState = pageStateResponse
    }

    const newState = {
      page,
      pageState: undefined
    }
    if (!maintainPresenter) {
      newState.activePresenter = undefined
    }

    this.set(_.defaults(extraState, newState), { replacePath })
  }

  joinPresenterSession (username) {
    this.changePage('live_response', { username })
  }

  showIndividualActivity (type, participantKey) {
    this.changePage('static_response', { type, participantKey })
  }

  enterRegisterFlow (username, { replacePath = false } = {}) {
    this.changePage('register', { username }, { replacePath, maintainPresenter: true })
  }

  messageHistory () {
    return getStorage(this.get('user'), 'message')
  }

  notifyPageLoaded (page) {
    if (page === this.page) {
      this.trigger('page:loaded', page)
    }
  }

  logout () {
    this.changePage('logout')
  }

  inspect () {
    const result = super.toJSON()

    // Deal with state model attributes whose values are complex types
    result.participant = this.get('participant')?.toJSON()
    result.settings = this.get('settings')?.toJSON()
    result.user = this.get('user')?.toJSON().user

    // session only has references to the participant and user, which we otherwise have anyway
    delete result.session

    return result
  }
}
